/* eslint-disable no-use-before-define */
import React, { useEffect, useState } from 'react'
import Typography from '@material-ui/core/Typography'
import { GripVertical } from 'lib/lucide'
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd'
import DOMPurify from 'dompurify'
import { useTranslation } from 'react-i18next'

import { Dialog, snackbar } from 'components'
import { ERRORS } from 'consts'
import { useApi, useApiCall } from 'hooks'

import { GetMembershipsResponse } from '../../Memberships/types'
import { getParkingDisplayOrder, updateParkingDisplayOrder } from '../api'
import { ParkingDisplayOrderMembership } from '../types'
import { useStyles } from './styles'

interface OrderParkingBenefitsDialogProps {
  handleClose: () => void
}

interface LevelTabIconProps {
  selected: boolean
}

const LevelTabIcon: React.FC<LevelTabIconProps> = ({ selected }) => (
  <svg width="20" height="20" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M21.1992 11C15.6767 11 11.1992 15.4774 11.1992 21H1.19922C1.19922 9.9549 10.1541 1 21.1992 1V11Z"
      fill={selected ? '#E0061A' : 'transparent'}
      stroke={selected ? '#E0061A' : '#1A171B'}
      strokeWidth="2"
    />
  </svg>
)

const getDisplayOrderResponseGetter = (responseData: GetMembershipsResponse) =>
  (responseData?.memberships ?? []).map(membership => ({
    id: membership.id,
    title: membership.title,
    level: membership.level ?? 0,
    prizes: (membership.prizes ?? [])
      .filter(prize => !prize.birthdayPrize)
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map(prize => ({
        id: prize.id,
        title: prize.title,
        additionalText: prize.additionalText,
        iconUrl: prize.iconUrl,
        order: prize.order ?? 0,
        parkingConfigLevelId: prize.parkingConfigLevelId ?? null,
      })),
  }))

const OrderParkingBenefitsDialog: React.FC<OrderParkingBenefitsDialogProps> = ({ handleClose }) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const [{ data: loadedMemberships, isLoading }] = useApi<
    GetMembershipsResponse,
    ParkingDisplayOrderMembership[]
  >(getParkingDisplayOrder, getDisplayOrderResponseGetter, {
    baseData: [],
    onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
  })
  const [updateOrderApi, isSaving] = useApiCall<
    { membershipId: number; prizeIds: number[] },
    void
  >(updateParkingDisplayOrder)

  const [memberships, setMemberships] = useState<ParkingDisplayOrderMembership[]>([])
  const [activeTab, setActiveTab] = useState(0)

  useEffect(() => {
    setMemberships(loadedMemberships)
    setActiveTab(current =>
      loadedMemberships.length && current >= loadedMemberships.length ? 0 : current,
    )
  }, [loadedMemberships])

  const selectedMembership = memberships[activeTab]

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !selectedMembership) return
    if (result.source.index === result.destination.index) return

    const prizes = Array.from(selectedMembership.prizes)
    const [moved] = prizes.splice(result.source.index, 1)
    prizes.splice(result.destination.index, 0, moved)

    setMemberships(
      memberships.map(membership =>
        membership.id === selectedMembership.id ? { ...membership, prizes } : membership,
      ),
    )
  }

  const handleSubmit = async () => {
    try {
      for (const membership of memberships) {
        // Sequential to keep the shared loading flag accurate and avoid racing writes.
        // eslint-disable-next-line no-await-in-loop
        await updateOrderApi({
          membershipId: membership.id,
          prizeIds: membership.prizes.map(prize => prize.id),
        })
      }
      handleClose()
    } catch (err) {
      console.error(err)
      snackbar.show(t('parking:errors.orderFailed') as string)
    }
  }

  return (
    <Dialog
      title={t('parking:order.title') as string}
      isOpen
      showActions
      onCancel={handleClose}
      onAccept={handleSubmit}
      okButtonText={t('parking:save') as string}
      okButtonProps={{ disabled: isLoading || isSaving || memberships.length === 0 }}
      isLoading={isLoading || isSaving}
      style={{ width: 460, height: 720, maxWidth: '100%' }}
      contentStyle={{
        width: '100%',
        height: 540,
        boxSizing: 'border-box',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: 24,
        paddingRight: 24,
      }}
    >
      <div className={classes.orderDialogBody}>
        <Typography className={classes.orderHint}>{t('parking:order.hint') as string}</Typography>
        <div className={classes.orderPreview}>
        <p className={classes.orderPreviewTitle}>{t('parking:order.previewTitle') as string}</p>
        <div className={classes.orderLevelTabs}>
          {memberships.map((membership, index) => (
            <button
              key={membership.id}
              type="button"
              className={`${classes.orderLevelTab} ${
                index === activeTab ? classes.orderLevelTabSelected : ''
              }`}
              onClick={() => setActiveTab(index)}
            >
              <span className={classes.orderLevelTabIcon}>
                <LevelTabIcon selected={index === activeTab} />
              </span>
              <span>{membership.title}</span>
            </button>
          ))}
        </div>
        <DragDropContext onDragEnd={handleDragEnd}>
          {selectedMembership && (
            <Droppable droppableId={`membership-${selectedMembership.id}`}>
              {provided => (
                <div
                  className={classes.orderBenefits}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {selectedMembership.prizes.length === 0 && (
                    <p className={classes.orderEmpty}>{t('parking:order.emptyLevel') as string}</p>
                  )}
                  {selectedMembership.prizes.map((prize, index) => (
                    <Draggable key={prize.id} draggableId={`prize-${prize.id}`} index={index}>
                      {(draggableProvided, snapshot) => (
                        <div
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          {...draggableProvided.dragHandleProps}
                          className={`${classes.orderBenefit} ${
                            snapshot.isDragging ? classes.orderBenefitDragging : ''
                          }`}
                          aria-label={t('parking:order.dragHandle') as string}
                        >
                          {prize.iconUrl ? (
                            <img src={prize.iconUrl} alt="" className={classes.orderBenefitIcon} />
                          ) : (
                            <span className={classes.orderBenefitIconPlaceholder} />
                          )}
                          <div className={classes.orderBenefitBody}>
                            <span
                              className={classes.orderBenefitTitle}
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(prize.title),
                              }}
                            />
                            {prize.additionalText && (
                              <span className={classes.orderBenefitSubtitle}>
                                {prize.additionalText}
                              </span>
                            )}
                          </div>
                          <span className={classes.orderDragHandle}>
                            <GripVertical size={16} />
                          </span>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          )}
        </DragDropContext>
        </div>
      </div>
    </Dialog>
  )
}

export { OrderParkingBenefitsDialog }
